<template>
    <index-page-layout>
        <template #button-area>
            <v-layout>
                <v-flex xs4 class="pl-2" align-self-end>
                    <date-interval-field
                        v-model="date"
                        :start-label="$t('Start_Date')"
                        :end-label="$t('End_Date')"
                        :disabled="allProcessing"
                        :label="$t('Login_At')"
                        @is-valid="setDateValid"
                    />
                </v-flex>
                <v-flex xs2 class="px-2 pb-2" align-self-end>
                    <selector-multiple-filter-items
                        v-model="statuses"
                        :item-list="statusList"
                        :disabled="allProcessing"
                        :label="$t('Status')"
                    />
                </v-flex>
                <v-flex xs4 class="px-2 pb-1" align-self-end>
                    <v-autocomplete
                        v-model="nodeGroups"
                        :disabled="allProcessing"
                        :items="nodeGroupList"
                        :label="$t('Node_Groups')"
                        :no-data-text="$t('Node_group_not_found')"
                        clearable
                        multiple
                        item-value="id"
                        item-text="name"
                        :search-input.sync="searchInput"
                        @input="searchInput = null"
                    />
                </v-flex>
                <v-flex xs5 class="px-2 pb-1" align-self-end>
                    <list-search-inputter
                        v-model="freeWord"
                        :placeholder="$t('example.login_history_search_word')"
                        :disabled="allProcessing"
                        :loading="searchProcessing"
                        :other-valid="!dateValid"
                        @click="searchLoginHistoryIfCanPress"
                    />
                </v-flex>
                <v-spacer />
                <v-flex align-self-top xs1>
                    <csv-output-dialog-button :disabled="allProcessing" @output-csv="csvOutput" />
                </v-flex>
            </v-layout>
        </template>
        <template #top-pagination-area>
            <v-layout wrap>
                <v-flex sm12 md2>
                    <label-results :count="state.loginRecordCount" />
                </v-flex>
                <v-flex text-sm-center sm12 md8>
                    <pagination v-model="pagingParam" :total-visible="12" :disabled="allProcessing" />
                </v-flex>
                <v-flex text-sm-right sm12 md2>
                    <per-page-selector v-model="pagingParam" :disabled="allProcessing" />
                </v-flex>
            </v-layout>
        </template>
        <template #end-pagination-area>
            <pagination v-model="pagingParam" :total-visible="12" :disabled="allProcessing" />
        </template>
        <v-layout>
            <v-flex xs12>
                <the-login-history-table
                    :records="state.loginRecords"
                    :record-count="state.loginRecordCount"
                    :processing="searchProcessing"
                    :fetch-success="state.search.isFetchSuccess"
                    :disabled="allProcessing"
                    :sort.sync="sort"
                    :rec-file-processing="recFileProcessing"
                    :show-rec-id="showRecFileId"
                    @click-rec-button="openRecFileDialog"
                />
                <the-lending-record-rec-file-dialog
                    :title="showRecFileDialogTitle"
                    :is-show.sync="isShowRecFileDialog"
                    :privilege-id-lending-record-id="showRecFileId"
                    :rec-file-items="state.recFilePath"
                />
            </v-flex>
        </v-layout>
    </index-page-layout>
</template>

<script lang="ts">
import Component from 'vue-class-component';
import dayjs from 'dayjs';
import { Vue, Watch, Prop } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import SortParam from '../../../utils/types/SortParam';
import { PagingParam, mergePagingParam } from '../../../utils/types/PagingParam';
import { queryParamDecodeToArray } from '../../../utils/types/RouterQueryParamDecode';
import { SearchConditionInputBuffer } from '../../../utils/types/SearchConditionInputBuffer';
import { ListSearchInputter } from '../../molecules';
import { DateIntervalField, LabelResults, Pagination, PerPageSelector, SelectorMultipleFilterItems } from '../../atoms';
import { TheLoginHistoryTable, CsvOutputDialogButton } from '../../molecules';
import { shallowCopy } from '../../../utils/types/utility';
import type {
    LoginRecordSearchInfo,
    LoginRecordState,
    LoginRecordColumn,
    LoginRecordStatus,
} from '../../../store/modules/login_record/types';
import { isLoginRecordStatus } from '../../../store/modules/login_record/types';
import type { NodeGroup, NodeGroupState } from '../../../store/modules/node_group/types';
import TheLendingRecordRecFileDialog from '../application_form/lending_record/TheLendingRecordRecFileDialog.vue';
import { AppContext } from '../../../store/context';
const StatusList = AppContext.LoginHistoryStatusList;
import { IndexPageLayout } from '../';

@Component({
    name: 'TheLoginHistoryListPane',
    components: {
        CsvOutputDialogButton,
        SelectorMultipleFilterItems,
        DateIntervalField,
        ListSearchInputter,
        LabelResults,
        Pagination,
        PerPageSelector,
        TheLoginHistoryTable,
        IndexPageLayout,
        TheLendingRecordRecFileDialog,
    },
})
export default class TheLoginHistoryListPane extends Vue {
    @State('loginRecord') state: LoginRecordState;
    @State('nodeGroup') nodeGroupState: NodeGroupState;
    @Action('search', { namespace: 'loginRecord' }) search;
    @Action('csv', { namespace: 'loginRecord' }) csv;
    @Action('recFile', { namespace: 'loginRecord' }) recFile;
    @Action('fetchAll', { namespace: 'nodeGroup' }) fetchAll;

    searchCondition = new SearchConditionInputBuffer({
        word: '',
        startDate: '',
        endDate: '',
        status: [],
        nodeGroupId: [],
    });
    sort = new SortParam<LoginRecordColumn>({
        column: 'loggedin_at',
        direction: 'desc',
    });
    @Watch('sort', { deep: true })
    onSortChanged() {
        this.callSearch();
    }
    tableParam: PagingParam = {
        currentPage: 1,
        perPage: 10,
        total: 0,
    };
    @Watch('tableParam', { deep: true })
    onTableParamChanged() {
        this.callSearch();
    }

    showPrivilegeId: number = -1;
    showPrivilegeDialogTitle: string = '';
    isShowRecordLogDialog: boolean = false;
    showLendingRecordLogId: number = -1;
    showLendingRecordLogDialogTitle: string = '';
    isShowRecFileDialog: boolean = false;
    showRecFileId: number = -1;
    showRecFileDialogTitle: string = '';
    dateValid = false;
    searchInput = null;

    /**
     * lifecycle hooks
     */
    async created() {
        if (this.$route.params.status) {
            const decoded = this.queryParamStatusDecode(this.$route.params.status);
            this.searchCondition.displayParam.status.splice(
                0,
                this.searchCondition.displayParam.status.length,
                ...decoded
            );
        }

        this.fetchAll();
        this.searchCondition.searchConditionChange();
        this.callSearch();
    }

    /**
     * getter, setter
     */
    get date() {
        return {
            start: this.searchCondition.displayParam.startDate,
            end: this.searchCondition.displayParam.endDate,
        };
    }
    set date(val) {
        this.searchCondition.displayParam.startDate = val.start;
        this.searchCondition.displayParam.endDate = val.end;
    }
    get freeWord(): string {
        return this.searchCondition.displayParam.word;
    }
    set freeWord(val: string) {
        this.searchCondition.displayParam.word = val;
    }
    get statuses(): Array<string> {
        return this.searchCondition.displayParam.status;
    }
    set statuses(val: Array<string>) {
        this.searchCondition.displayParam.status = val;
    }
    get statusList(): Array<{ text_key: string; value: string }> {
        return StatusList;
    }
    get pagingParam(): PagingParam {
        return mergePagingParam(this.tableParam, { total: this.state.loginRecordCount });
    }
    set pagingParam(newVal: PagingParam) {
        this.tableParam = newVal;
    }
    get allProcessing(): boolean {
        return this.searchProcessing || this.csvProcessing || this.recFileProcessing;
    }
    get searchProcessing(): boolean {
        return this.state.search ? this.state.search.isProcessing : false;
    }
    get csvProcessing(): boolean {
        return this.state.csv ? this.state.csv.isProcessing : false;
    }
    get recFileProcessing(): boolean {
        return this.state.recFile ? this.state.recFile.isProcessing : false;
    }
    get nodeGroups(): Array<number> {
        return this.searchCondition.displayParam.nodeGroupId;
    }
    set nodeGroups(val: Array<number>) {
        this.searchCondition.displayParam.nodeGroupId = val;
    }
    get nodeGroupList(): Array<NodeGroup> {
        const notAffiliationGroup: NodeGroup = {
            id: 0,
            name: this.$t('Not_Affiliation'),
            description: '',
            created_at: '',
            updated_at: '',
        };
        let list = [notAffiliationGroup];
        if (!this.nodeGroupState.nodeGroups) {
            return list;
        }
        return list.concat(this.nodeGroupState.nodeGroups);
    }

    /**
     * methods
     */
    async searchLoginHistoryIfCanPress() {
        if (this.state.search.isProcessing) {
            return;
        }
        if (!this.dateValid) {
            return;
        }

        await this.searchLoginHistory();
    }
    async searchLoginHistory() {
        this.searchCondition.searchConditionChange();
        const searchParams = this.searchCondition.currentSearchParam();
        this.tableParam.currentPage = 1;
        await this.search({
            searchItems: searchParams,
            sort: this.sort,
            pageNumber: 1,
            perPage: this.tableParam.perPage,
        });
    }
    async callSearch() {
        if (this.searchProcessing) return;
        const searchParams = this.searchCondition.currentSearchParam();
        await this.search({
            searchItems: searchParams,
            pageNumber: this.tableParam.currentPage,
            perPage: this.tableParam.perPage,
            sort: this.sort,
        });
    }
    csvOutput() {
        if (this.state.search.isProcessing) return;
        const searchParams = this.searchCondition.currentSearchParam();
        this.csv({
            searchItems: searchParams,
            sort: this.sort,
        });
    }
    queryParamStatusDecode(param: unknown): Array<LoginRecordStatus> {
        return queryParamDecodeToArray(param, isLoginRecordStatus);
    }
    setDateValid(valid: boolean): void {
        this.dateValid = valid;
    }
    openRecFileDialog(item: LoginRecordSearchInfo) {
        const loginAt = dayjs(new Date(item.loggedin_at)).format((this as any).$t('format.ymdhm'));

        this.showRecFileId = item.id;
        this.showRecFileDialogTitle = `${loginAt} [ ${item.node_account_name} ] [ ${item.node_name} ]`;

        this.fetchRecFile(this.showRecFileId);
    }
    async fetchRecFile(id: number) {
        await this.recFile({ id: id });
        if (this.state.recFile.error || this.state.recFile.isFetchSuccess) {
            this.isShowRecFileDialog = true;
        }
    }
}
</script>
